import QRCode from "qrcode";
import { toDocumentFormat, toTime } from "helpers/dateHelper";

/**
 * Грузобагажная накладная — печатная HTML-форма, открывается в отдельном окне
 * и печатается/сохраняется в PDF силами браузера (точное соответствие шаблону
 * без серверной генерации PDF).
 *
 * Поля, которых пока нет в модели, заполняются значениями по умолчанию:
 *   • Доставка до двери (D2D) → «Нет»
 *   • Адрес доставки         → «До востребования (в отделении)»
 *   • № накладной            → SH-<id отправки, дополненный до 6 цифр>
 *   • ФИО менеджера          → пустая строка подписи
 * Телефон отправителя берётся из customer.user.phone (подгружается на детальной).
 */

const BRAND = "Ubee AI";
const D2D_DEFAULT = "Нет";
const ADDRESS_DEFAULT = "До востребования (в отделении)";

type QuoteFn = (payload: any) => Promise<{ data: any }>;

const up = (s: any) => String(s ?? "").toUpperCase();

const esc = (s: any) =>
  String(s ?? "").replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );

const money = (v: any) =>
  `${Number(v || 0).toLocaleString("ru-RU", { maximumFractionDigits: 3 })} ₸`;

const num = (v: any, frac = 4) =>
  Number(v || 0).toLocaleString("ru-RU", { maximumFractionDigits: frac });

// Склонение «блок/блока/блоков» под количество.
const pluralBlocks = (n: number) => {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return "блок";
  if (n10 >= 2 && n10 <= 4 && (n100 < 10 || n100 >= 20)) return "блока";
  return "блоков";
};

const customerName = (c: any) =>
  c ? `${c.lastName || ""} ${c.firstName || ""}`.trim() || c.name || "—" : "—";

const waybillNumber = (trip: any) => `UBEE-${String(trip?.id ?? "").padStart(6, "0")}`;

/** Собрать самодостаточный HTML-документ накладной. */
export function renderWaybillHtml(trip: any, breakdown: any, qrDataUrl: string): string {
  const pieces: any[] = trip?.baggages || [];
  const totalWeight = pieces.reduce((s, p) => s + (Number(p.weight) || 0), 0);

  const descriptions = Array.from(
    new Set(pieces.map((p) => (p.description || "").trim()).filter(Boolean)),
  );
  const types = Array.from(new Set(pieces.map((p) => (p.type || "").trim()).filter(Boolean)));
  const cargoDesc = descriptions.length ? descriptions.join(", ") : types.join(", ") || "—";

  const isLegal = trip?.clientType === "legal";
  const sender = customerName(trip?.customer);
  const senderPhone = trip?.customer?.user?.phone ? String(trip.customer.user.phone) : "—";

  const hasRecipient = !!(trip?.recipientName || "").trim();
  const recipientName = hasRecipient ? trip.recipientName : sender;
  const recipientPhone = hasRecipient ? trip?.recipientPhone || "—" : senderPhone;

  const hasDV = trip?.declaredValue && Number(trip?.declaredValueAmount) > 0;
  const declaredValueText = hasDV ? money(trip.declaredValueAmount) : "—";

  const dt = trip?.paidAt || trip?.createdAt;
  const dateStr = dt ? `${toDocumentFormat(dt)}, ${toTime(dt)}` : "—";

  const blocks = Number(breakdown?.blocks || 0);
  const carriage = Number(breakdown?.carriage || 0);
  const rate = blocks ? carriage / blocks : 0;
  const waybillFee = Number(breakdown?.waybillFee || 0);
  const declaredFee = Number(breakdown?.declaredValueFee || 0);
  const discountAmount = Number(breakdown?.discountAmount || 0);
  const discountPercent = Number(breakdown?.discountPercent || 0);
  const total = Number(trip?.totalPrice ?? breakdown?.total ?? 0);

  const calcWeight = blocks > 0 ? `${blocks * 10} кг (блоки по 10 кг)` : `${totalWeight} кг`;
  const waybillNo = waybillNumber(trip);

  const origin = up(trip?.originStation?.name || "—");
  const destination = up(trip?.destinationStation?.name || "—");

  const costRows: string[] = [
    `<tr>
       <td class="bold">Оформление накладной (фиксированный сбор)</td>
       <td>Печатная форма, регистрация в системе</td>
       <td class="num">${money(waybillFee)}</td>
     </tr>`,
    `<tr>
       <td class="bold">Тариф за перевозку (за вес)</td>
       <td>${blocks} ${pluralBlocks(blocks)} по 10 кг × ${num(rate)} ₸ / блок</td>
       <td class="num">${money(carriage)}</td>
     </tr>`,
  ];
  if (discountAmount > 0) {
    costRows.push(
      `<tr>
         <td class="bold">Скидка по билету (−${num(discountPercent, 0)}%)</td>
         <td>Скидка 50% на перевозку при наличии билета</td>
         <td class="num">−${money(discountAmount)}</td>
       </tr>`,
    );
  }
  if (hasDV && declaredFee > 0) {
    costRows.push(
      `<tr>
         <td class="bold">Страховой сбор (за объявленную ценность)</td>
         <td>Страховое покрытие при утере или повреждении груза</td>
         <td class="num">${money(declaredFee)}</td>
       </tr>`,
    );
  }

  return `<!doctype html>
<html lang="ru">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Накладная ${esc(waybillNo)}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: Arial, "Helvetica Neue", Helvetica, sans-serif;
    color: #1a1a1a;
    font-size: 12px;
    line-height: 1.45;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .wb {
    max-width: 760px;
    margin: 0 auto;
    padding: 8px 4px 24px;
  }
  .wb-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
  .wb-brand { color: #2348a0; font-weight: 800; font-size: 22px; letter-spacing: -0.02em; }
  .wb-head-center { text-align: center; flex: 1; padding-top: 2px; }
  .wb-title { font-weight: 800; font-size: 17px; letter-spacing: 0.02em; }
  .wb-number { color: #2348a0; font-weight: 800; font-size: 16px; margin-top: 2px; }
  .wb-qr { width: 74px; height: 74px; display: block; }
  .wb-rule { border: 0; border-top: 2px solid #1a1a1a; margin: 12px 0 16px; }

  .wb-route {
    border: 1px solid #d9d9d6; border-radius: 10px; padding: 14px;
    text-align: center; font-weight: 800; font-size: 14px; letter-spacing: 0.01em;
  }
  .wb-route .arrow { color: #2348a0; margin: 0 12px; font-weight: 700; }

  .wb-cards { display: flex; gap: 16px; margin-top: 16px; }
  .wb-card { flex: 1; border: 1px solid #e2e1dd; border-radius: 10px; padding: 12px 14px; }
  .wb-card-title {
    font-size: 10px; font-weight: 800; letter-spacing: 0.08em; color: #5b5b58;
    border-bottom: 1px dashed #c9c8c3; padding-bottom: 6px; margin-bottom: 8px;
  }
  .wb-row { display: flex; justify-content: space-between; gap: 12px; padding: 3px 0; font-size: 11.5px; }
  .wb-row .k { color: #6b6b67; }
  .wb-row .v { font-weight: 700; text-align: right; }

  .wb-table { width: 100%; border-collapse: collapse; margin-top: 18px; }
  .wb-table th {
    text-align: left; font-size: 9px; font-weight: 800; letter-spacing: 0.04em;
    color: #8a8a85; text-transform: uppercase; border: 1px solid #e2e1dd;
    background: #f6f6f4; padding: 7px 8px; vertical-align: top;
  }
  .wb-table td { border: 1px solid #e2e1dd; padding: 9px 8px; font-size: 11.5px; vertical-align: top; }
  .wb-table td.bold { font-weight: 700; }

  .wb-detail-title {
    color: #2348a0; font-size: 11px; font-weight: 800; letter-spacing: 0.06em;
    text-transform: uppercase; margin: 22px 0 8px;
  }
  .wb-cost { width: 100%; border-collapse: collapse; }
  .wb-cost th, .wb-cost td { border: 1px solid #e2e1dd; padding: 9px 10px; font-size: 11.5px; }
  .wb-cost th {
    text-align: left; font-size: 9px; font-weight: 800; letter-spacing: 0.04em;
    color: #8a8a85; text-transform: uppercase; background: #f6f6f4;
  }
  .wb-cost .num { text-align: right; white-space: nowrap; }
  .wb-cost .bold { font-weight: 700; }
  .wb-cost .total td { font-weight: 800; font-size: 13px; }
  .wb-cost .total .num { color: #2348a0; }

  .wb-sign { display: flex; gap: 48px; margin-top: 46px; }
  .wb-sign .col { flex: 1; }
  .wb-sign .line { border-top: 1px solid #1a1a1a; padding-top: 6px; font-size: 10px; color: #6b6b67; }

  .wb-foot {
    text-align: center; color: #9a9a96; font-size: 9.5px; margin-top: 34px;
    border-top: 1px dashed #d9d9d6; padding-top: 12px;
  }
</style>
</head>
<body>
  <div class="wb">
    <div class="wb-head">
      <div class="wb-brand">${esc(BRAND)}</div>
      <div class="wb-head-center">
        <div class="wb-title">ГРУЗОБАГАЖНАЯ НАКЛАДНАЯ</div>
        <div class="wb-number">${esc(waybillNo)}</div>
      </div>
      <img class="wb-qr" src="${qrDataUrl}" alt="QR ${esc(waybillNo)}" />
    </div>
    <hr class="wb-rule" />

    <div class="wb-route">
      ${esc(origin)} <span class="arrow">➔</span> ${esc(destination)}
    </div>

    <div class="wb-cards">
      <div class="wb-card">
        <div class="wb-card-title">ОТПРАВИТЕЛЬ</div>
        <div class="wb-row"><span class="k">Наименование / ФИО:</span><span class="v">${esc(up(sender))}</span></div>
        <div class="wb-row"><span class="k">БИН компании:</span><span class="v">${esc(trip?.companyBin || "—")}</span></div>
        <div class="wb-row"><span class="k">Телефон:</span><span class="v">${esc(senderPhone)}</span></div>
        <div class="wb-row"><span class="k">Тип клиента:</span><span class="v">${isLegal ? "Юридическое лицо" : "Физическое лицо"}</span></div>
      </div>
      <div class="wb-card">
        <div class="wb-card-title">ПОЛУЧАТЕЛЬ</div>
        <div class="wb-row"><span class="k">ФИО получателя:</span><span class="v">${esc(up(recipientName))}</span></div>
        <div class="wb-row"><span class="k">Телефон:</span><span class="v">${esc(recipientPhone)}</span></div>
        <div class="wb-row"><span class="k">Доставка до двери (D2D):</span><span class="v">${D2D_DEFAULT}</span></div>
        <div class="wb-row"><span class="k">Адрес доставки:</span><span class="v">${ADDRESS_DEFAULT}</span></div>
      </div>
    </div>

    <table class="wb-table">
      <thead>
        <tr>
          <th>Описание груза</th>
          <th>Фактический вес</th>
          <th>Расчетный вес (округленный)</th>
          <th>Колво мест</th>
          <th>Объявленная ценность</th>
          <th>Дата и время оформления</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="bold">${esc(cargoDesc)}</td>
          <td>${esc(totalWeight)} кг</td>
          <td>${esc(calcWeight)}</td>
          <td>${esc(pieces.length)} шт</td>
          <td>${esc(declaredValueText)}</td>
          <td>${esc(dateStr)}</td>
        </tr>
      </tbody>
    </table>

    <div class="wb-detail-title">Детализация расчета стоимости</div>
    <table class="wb-cost">
      <thead>
        <tr>
          <th>Наименование сбора / услуги</th>
          <th>Описание тарифа</th>
          <th class="num">Сумма</th>
        </tr>
      </thead>
      <tbody>
        ${costRows.join("\n        ")}
        <tr class="total">
          <td colspan="2">Итого к оплате (всего):</td>
          <td class="num">${money(total)}</td>
        </tr>
      </tbody>
    </table>

    <div class="wb-sign">
      <div class="col"><div class="line">Менеджер оформивший (подпись / ФИО)</div></div>
      <div class="col"><div class="line">Клиент отправитель (подпись / расшифровка)</div></div>
    </div>

    <div class="wb-foot">
      Благодарим за то, что выбрали наш сервис! Распечатано автоматически из информационной системы ${esc(BRAND)}.
    </div>
  </div>
  <script>
    window.addEventListener("load", function () {
      setTimeout(function () { window.focus(); window.print(); }, 250);
    });
  </script>
</body>
</html>`;
}

/**
 * Открыть накладную в новом окне и отправить на печать.
 * Окно открывается синхронно (в обработчике клика), чтобы не сработал блокировщик
 * всплывающих окон; тариф (детализация расчёта) берётся с бэкенда через quote.
 */
export async function openWaybill(trip: any, quote: QuoteFn): Promise<void> {
  const win = window.open("", "_blank");
  try {
    const pieces: any[] = trip?.baggages || [];
    const totalWeight = pieces.reduce((s, p) => s + (Number(p.weight) || 0), 0);
    const hasDV = !!(trip?.declaredValue && Number(trip?.declaredValueAmount) > 0);
    const waybillNo = waybillNumber(trip);

    const [quoteRes, qrDataUrl] = await Promise.all([
      quote({
        totalWeight,
        pieceCount: pieces.length,
        declaredValue: hasDV,
        ticketNumber: trip?.ticketNumber || "",
      }),
      QRCode.toDataURL(waybillNo, { width: 240, margin: 0 }),
    ]);

    const html = renderWaybillHtml(trip, quoteRes?.data, qrDataUrl);

    if (win) {
      win.document.open();
      win.document.write(html);
      win.document.close();
    } else {
      // Всплывающее окно заблокировано — отдаём накладную файлом, чтобы кнопка не «молчала».
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Накладная ${waybillNo}.html`;
      a.click();
      URL.revokeObjectURL(url);
    }
  } catch (ex) {
    if (win) win.close();
    throw ex;
  }
}
